import { type ReactNode } from 'react';

import { CheckIcon, ChevronDownIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import {
  DropdownMenu,
  type DropdownMenuOption,
  type DropdownMenuProps,
  renderDropdownItems,
} from '@air/primitive-dropdown-menu';
import { Spinner } from '@air/primitive-spinner';

export type DropdownMenuWithSelectedLabelButtonProps = {
  trigger?: DropdownMenuProps['trigger'];
  selectedOption: {
    id: string;
    label: ReactNode;
  };
  buttonClassname?: string;
  testId?: string;
  /** Set this to true if you want to just show text of the selected item and not allow the dropdown to be used */
  disabled?: boolean;
  options: DropdownMenuOption[];
  isLoading?: boolean;
} & Omit<DropdownMenuProps, 'children' | 'trigger'>;

export const DropdownMenuWithSelectedLabelButton = ({
  disabled = false,
  isLoading = false,
  options,
  selectedOption,
  testId,
  ...rest
}: DropdownMenuWithSelectedLabelButtonProps) => {
  if (disabled || isLoading) {
    return (
      <div className="flex items-center gap-1 px-2 py-1.5 ads:text-metadata text-14 text-gray-9">
        {selectedOption.label}
        {isLoading && <Spinner className="size-4" />}
      </div>
    );
  }

  const optionsWithAdornment: DropdownMenuOption[] = options.map((option) =>
    option.id === selectedOption.id
      ? {
          ...option,
          suffix: (
            <CheckIcon size="sm" className="shrink-0 ads:text-icon-primary text-blue-10 ui2026:text-altitude-11" />
          ),
        }
      : option,
  );

  const trigger: ReactNode = (
    <Button appearance="ghost" color="gray" size="small" data-testid={testId}>
      <span className="font-normal ads:text-primary text-14 text-gray-12">{selectedOption.label}</span>
      <div className="ml-1 flex items-center">
        <ChevronDownIcon size="sm" className="ads:text-metadata text-gray-9" />
      </div>
    </Button>
  );

  return (
    <DropdownMenu trigger={trigger} {...rest}>
      {renderDropdownItems({ options: optionsWithAdornment })}
    </DropdownMenu>
  );
};
